import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronLeft, ChevronRight, RefreshCw, BookOpen, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TutorCard from '../components/TutorCard';
import { tutorSearchService } from '../services/tutorSearchService';
import { tutorService } from '../services/tutorService';
import { studentService } from '../services/studentService';
import { TutorCardSkeleton } from '../components/ui/skeletons';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const PER_PAGE = 9;

const SearchResults = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { customer } = useCustomerAuth();
  
  const initialResults = location.state?.results || [];
  const initialFilters = location.state?.filters || {};

  const [tutors, setTutors] = useState(initialResults);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(initialFilters.classId || 'all');
  const [studentClasses, setStudentClasses] = useState([]);
  const [students, setStudents] = useState([]);
  
  const [userLocation, setUserLocation] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(initialResults.length);

  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchClasses();
    getUserLocation();
  }, []);

  useEffect(() => {
    if (customer) {
      fetchStudents();
    }
  }, [customer]);

  useEffect(() => {
    searchTutors();
  }, [currentPage, userLocation]);

  const fetchClasses = async () => {
    try {
      const response = await tutorService.getClasses();
      setClasses(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await studentService.getStudents();
      const studentList = response.data?.students || response.students || response || [];
      setStudents(studentList);
      
      const uniqueClassIds = [...new Set(
        studentList
          .map(s => s.class)
          .filter(c => c && !isNaN(parseInt(c)))
          .map(c => parseInt(c))
      )];
      setStudentClasses(uniqueClassIds);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const getUserLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Could not get user location:', error.message);
        },
        { enableHighAccuracy: false, timeout: 5000 }
      );
    }
  };

  const searchTutors = async (page = currentPage) => {
    setLoading(true);
    setError(null);
    
    try {
      const filters = {
        page: page,
        per_page: PER_PAGE
      };
      
      if (userLocation) {
        filters.latitude = userLocation.latitude;
        filters.longitude = userLocation.longitude;
      }
      
      if (selectedClassId && selectedClassId !== 'all') {
        filters.class = parseInt(selectedClassId);
      } else if (studentClasses.length > 0 && customer) {
        filters.classes = studentClasses;
      }

      const { tutors: results, pagination } = await tutorSearchService.searchTutors(filters);
      
      setTutors(results);
      setTotalResults(pagination.total || results.length);
      setTotalPages(pagination.last_page || 1);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to search tutors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    searchTutors(1);
  };

  const handleClearFilters = () => {
    setSelectedClassId('all');
    setCurrentPage(1);
    searchTutors(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const hasActiveFilters = selectedClassId && selectedClassId !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Tutors</h1>
          <p className="text-gray-600">
            Find the perfect tutor for your learning needs
          </p>
          {customer && studentClasses.length > 0 && !hasActiveFilters && (
            <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              Showing tutors for your children's classes
            </p>
          )}
          {userLocation && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Using your location to find nearby tutors
            </p>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {showFilters && (
            <div className="lg:w-72 flex-shrink-0">
              <Card className="p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Class
                    </label>
                    <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id?.toString()}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleSearch}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Search
                  </Button>
                </div>
              </Card>
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
                
                {!loading && hasActiveFilters && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {selectedClassId && selectedClassId !== 'all' && (
                      <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                        <BookOpen className="h-3 w-3 mr-1" />
                        {classes.find(c => c.id?.toString() === selectedClassId)?.name || 'Class'}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {!loading && totalResults > 0 && (
                <span className="text-sm text-gray-600">
                  {totalResults} tutor{totalResults !== 1 ? 's' : ''} found
                </span>
              )}
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <TutorCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? (
              <Card className="p-8 text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={handleSearch} variant="outline" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </Card>
            ) : tutors.length === 0 ? (
              <Card className="p-12 text-center">
                <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No Tutors Found
                </h3>
                <p className="text-gray-500 mb-4">
                  We couldn't find any tutors matching your criteria. Try adjusting your filters.
                </p>
                <Button onClick={handleClearFilters} variant="outline">
                  Clear Filters
                </Button>
              </Card>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tutors.map((tutor) => (
                    <TutorCard key={tutor.id} tutor={tutor} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className={currentPage === pageNum ? "bg-blue-600" : ""}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
